import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  Nightlife,
  BusinessCenter,
  Speed,
  ArrowForward,
  Album,
  Headphones,
  Wifi
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import logoImage from '../../assets/setsync_logo_transparent.png';
import Scanlines from '../ui/Scanlines';
import FeatureCard from '../ui/FeatureCard';

// Lightning Bolt Effect - Cyberpunk style
const LightningBolt = ({ delay = 0, angle = 0 }) => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 0,
      height: 0,
      transform: `rotate(${angle}deg)`,
      transformOrigin: 'center center',
    }}
  >
    {/* Main lightning bolt using SVG path for jagged effect */}
    <Box
      component="svg"
      viewBox="0 0 30 120"
      sx={{
        position: 'absolute',
        left: '-15px',
        top: '-180px',
        width: '30px',
        height: '120px',
        opacity: 0,
        filter: 'drop-shadow(0 0 8px #FF6B1A) drop-shadow(0 0 15px #FFAA00)',
        animation: `lightningFlash 2.5s ease-out ${delay}s infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
        '@keyframes lightningFlash': {
          '0%': { opacity: 0, transform: 'translateY(60px) scaleY(0.3)' },
          '5%': { opacity: 1 },
          '8%': { opacity: 0.3 },
          '12%': { opacity: 1 },
          '15%': { opacity: 0.5 },
          '20%': { opacity: 1, transform: 'translateY(0) scaleY(1)' },
          '50%': { opacity: 0.8, transform: 'translateY(-30px) scaleY(1.1)' },
          '100%': { opacity: 0, transform: 'translateY(-60px) scaleY(0.5)' },
        },
      }}
    >
      <path
        d="M15 0 L18 20 L25 22 L12 50 L18 52 L8 80 L15 82 L5 110 L10 85 L3 83 L13 55 L7 53 L20 25 L12 23 L15 0"
        fill="url(#lightningGradient)"
        stroke="#FFAA00"
        strokeWidth="1"
      />
      <defs>
        <linearGradient id="lightningGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFDD00" />
          <stop offset="30%" stopColor="#FF6B1A" />
          <stop offset="100%" stopColor="#FF4400" stopOpacity="0.5" />
        </linearGradient>
      </defs>
    </Box>
    {/* Secondary bolt for depth */}
    <Box
      component="svg"
      viewBox="0 0 20 80"
      sx={{
        position: 'absolute',
        left: '5px',
        top: '-150px',
        width: '20px',
        height: '80px',
        opacity: 0,
        filter: 'drop-shadow(0 0 5px #FF6B1A)',
        animation: `lightningFlash2 2.5s ease-out ${delay + 0.1}s infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
        '@keyframes lightningFlash2': {
          '0%': { opacity: 0 },
          '8%': { opacity: 0.6 },
          '12%': { opacity: 0 },
          '16%': { opacity: 0.8 },
          '25%': { opacity: 0.4 },
          '100%': { opacity: 0 },
        },
      }}
    >
      <path
        d="M10 0 L12 15 L18 17 L8 40 L14 42 L5 70 L9 45 L4 43 L12 20 L7 18 L10 0"
        fill="#FF6B1A"
        opacity="0.7"
      />
    </Box>
  </Box>
);

// Binary data stream overlay for cyberpunk effect
const BinaryStream = ({ size }) => {
  const binaryPattern = "10110100 01101001 11010010 00101101 10010110 01011010 11001011 00110101";
  
  return (
    <Box
      sx={{
        position: 'absolute',
        width: size * 0.7,
        height: size * 0.7,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'binaryRotate 20s linear infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        '@keyframes binaryRotate': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(-360deg)' },
        },
      }}
    >
      {/* Concentric rings of binary */}
      {[0.9, 0.75, 0.6, 0.45].map((scale, ringIndex) => (
        <Box
          key={ringIndex}
          sx={{
            position: 'absolute',
            width: `${scale * 100}%`,
            height: `${scale * 100}%`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Binary characters positioned around the ring */}
          {Array.from({ length: 16 + ringIndex * 4 }).map((_, i, arr) => {
            const angle = (360 / arr.length) * i;
            const char = binaryPattern[(i + ringIndex * 7) % binaryPattern.length];
            if (char === ' ') return null;
            return (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  transform: `rotate(${angle}deg) translateY(-${scale * size * 0.35}px)`,
                  fontFamily: '"Courier New", monospace',
                  fontSize: `${8 + ringIndex}px`,
                  color: i % 3 === 0 ? '#FF6B1A' : 'rgba(255, 107, 26, 0.4)',
                  textShadow: i % 3 === 0 ? '0 0 10px #FF6B1A' : 'none',
                  opacity: 0.6 + (ringIndex * 0.1),
                  animation: i % 5 === 0 ? 'binaryGlow 1.5s ease-in-out infinite' : 'none',
                  animationDelay: `${i * 0.1}s`,
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  '@keyframes binaryGlow': {
                    '0%, 100%': { opacity: 0.4, textShadow: 'none' },
                    '50%': { opacity: 1, textShadow: '0 0 15px #FF6B1A, 0 0 25px #FFAA00' },
                  },
                }}
              >
                {char}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

// Vinyl Record Animation with Lightning and Binary - Cyberpunk Style
const VinylRecord = ({ size = 400, spinning = true }) => (
  <Box
    sx={{
      position: 'relative',
      width: size * 1.5,
      height: size * 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {/* Lightning Bolts - Cyberpunk broadcast effect */}
    <LightningBolt delay={0} angle={-25} />
    <LightningBolt delay={0.5} angle={20} />
    <LightningBolt delay={1.0} angle={55} />
    <LightningBolt delay={1.5} angle={-55} />
    <LightningBolt delay={2.0} angle={85} />
    <LightningBolt delay={0.25} angle={-85} />
    
    {/* Glow Ring with glitch effect */}
    <Box
      sx={{
        position: 'absolute',
        width: size + 40,
        height: size + 40,
        borderRadius: '50%',
        border: '2px solid rgba(255, 107, 26, 0.3)',
        boxShadow: '0 0 60px rgba(255, 107, 26, 0.4), inset 0 0 60px rgba(255, 107, 26, 0.1)',
        animation: 'pulseGlow 3s ease-in-out infinite, glitchRing 8s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        '@keyframes pulseGlow': {
          '0%, 100%': { 
            boxShadow: '0 0 60px rgba(255, 107, 26, 0.4), inset 0 0 60px rgba(255, 107, 26, 0.1)',
            transform: 'scale(1)',
          },
          '50%': { 
            boxShadow: '0 0 100px rgba(255, 107, 26, 0.6), inset 0 0 80px rgba(255, 107, 26, 0.2)',
            transform: 'scale(1.02)',
          },
        },
        '@keyframes glitchRing': {
          '0%, 90%, 100%': { opacity: 1 },
          '92%': { opacity: 0.8, transform: 'scale(1) translateX(2px)' },
          '94%': { opacity: 1, transform: 'scale(1.01) translateX(-2px)' },
          '96%': { opacity: 0.9, transform: 'scale(1) translateX(1px)' },
        },
      }}
    />
    
    {/* Main Record with cyberpunk grooves */}
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `
          radial-gradient(circle at center, 
            #FF6B1A 0%, 
            #FF6B1A 8%, 
            #0A0A0A 8%, 
            #0A0A0A 10%,
            #151515 10%,
            #151515 12%,
            #0D0D0D 12%,
            #0D0D0D 100%
          )
        `,
        position: 'relative',
        animation: spinning ? 'spin 6s linear infinite' : 'none',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        boxShadow: '0 0 80px rgba(255, 107, 26, 0.5), 0 0 120px rgba(255, 107, 26, 0.3), inset 0 0 30px rgba(0,0,0,0.8)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '12%',
          left: '12%',
          right: '12%',
          bottom: '12%',
          borderRadius: '50%',
          background: `
            repeating-radial-gradient(
              circle at center, 
              transparent 0px, 
              transparent 3px, 
              rgba(255, 107, 26, 0.08) 3px, 
              rgba(255, 107, 26, 0.08) 4px,
              transparent 4px,
              transparent 8px,
              rgba(255, 107, 26, 0.05) 8px,
              rgba(255, 107, 26, 0.05) 9px
            )
          `,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-5%',
          left: '-5%',
          right: '-5%',
          bottom: '-5%',
          borderRadius: '50%',
          background: `
            conic-gradient(
              from 0deg, 
              transparent 0deg, 
              rgba(255, 107, 26, 0.4) 5deg,
              rgba(255, 200, 0, 0.2) 10deg,
              transparent 20deg, 
              transparent 180deg,
              rgba(255, 107, 26, 0.2) 185deg,
              transparent 195deg,
              transparent 360deg
            )
          `,
          animation: 'spinReverse 3s linear infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          filter: 'blur(2px)',
        },
        '@keyframes spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
        '@keyframes spinReverse': {
          '0%': { transform: 'rotate(360deg)' },
          '100%': { transform: 'rotate(0deg)' },
        },
      }}
    >
      {/* Binary data streams on the record */}
      <BinaryStream size={size} />
      
      {/* Data scan line effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '50%',
          background: 'linear-gradient(180deg, transparent 48%, rgba(255, 107, 26, 0.15) 50%, transparent 52%)',
          animation: 'scanLine 2s linear infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          '@keyframes scanLine': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
    </Box>
  </Box>
);

// Retro Grid Background
const RetroGrid = () => (
  <Box
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '50vh',
      background: `
        linear-gradient(to bottom, transparent 0%, #0D0D0D 100%),
        linear-gradient(90deg, rgba(255, 107, 26, 0.1) 1px, transparent 1px),
        linear-gradient(rgba(255, 107, 26, 0.1) 1px, transparent 1px)
      `,
      backgroundSize: '100% 100%, 40px 40px, 40px 40px',
      transform: 'perspective(500px) rotateX(60deg)',
      transformOrigin: 'bottom center',
      pointerEvents: 'none',
      opacity: 0.4,
    }}
  />
);

// Glitch Text Effect
const GlitchText = ({ children }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'inline-block',
      '&::before, &::after': {
        content: `"${children}"`,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&::before, &::after': { animation: 'none', opacity: 0 },
      },
      '&::before': {
        color: 'primary.main',
        animation: 'glitch1 2s infinite',
        clipPath: 'polygon(0 0, 100% 0, 100% 45%, 0 45%)',
        transform: 'translate(-2px, -2px)',
        opacity: 0.8,
      },
      '&::after': {
        color: 'info.main',
        animation: 'glitch2 2s infinite',
        clipPath: 'polygon(0 55%, 100% 55%, 100% 100%, 0 100%)',
        transform: 'translate(2px, 2px)',
        opacity: 0.8,
      },
      '@keyframes glitch1': {
        '0%, 90%, 100%': { transform: 'translate(0)' },
        '92%': { transform: 'translate(-3px, 1px)' },
        '94%': { transform: 'translate(3px, -1px)' },
        '96%': { transform: 'translate(-2px, 2px)' },
      },
      '@keyframes glitch2': {
        '0%, 90%, 100%': { transform: 'translate(0)' },
        '91%': { transform: 'translate(2px, -1px)' },
        '93%': { transform: 'translate(-3px, 2px)' },
        '95%': { transform: 'translate(2px, 1px)' },
      },
    }}
  >
    {children}
  </Box>
);

// FeatureCard imported from components/ui/FeatureCard

// Stats Component with Retro LCD Display Style
const StatDisplay = ({ count, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography 
      sx={{ 
        fontFamily: '"Bebas Neue", sans-serif',
        fontSize: { xs: '3rem', md: '4rem' },
        color: 'primary.main',
        lineHeight: 1,
        textShadow: '0 0 20px rgba(255, 107, 26, 0.5)',
      }}
    >
      {count}+
    </Typography>
    <Typography 
      sx={{ 
        fontFamily: '"Space Mono", monospace',
        color: 'text.secondary',
        fontSize: '0.75rem',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        mt: 1,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userCounts, setUserCounts] = useState({ djCount: 0, venueCount: 0 });
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchUserCounts = async () => {
      try {
        const response = await axios.get('/api/users/counts');
        setUserCounts(response.data);
      } catch (error) {
        console.error('Error fetching user counts:', error);
        setUserCounts({ djCount: 0, venueCount: 0 });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserCounts();
  }, []);

  const features = [
    {
      icon: <Nightlife />,
      title: 'For Venues',
      description: 'Add live DJ programming on off-nights, for cancellations, and when an in-room booking is impractical.',
    },
    {
      icon: <Headphones />,
      title: 'For DJs',
      description: 'Create new paid hours through guest sets, cross-market collaborations, and remote residencies.',
    },
    {
      icon: <BusinessCenter />,
      title: 'Local First',
      description: 'Designed to complement—not displace—already-booked or established local DJs.',
    },
  ];

  const capabilities = [
    {
      icon: <Speed />,
      title: 'Zero Latency',
      description: 'Near real-time streaming. Your drops land exactly when they should. Every time.',
    },
    {
      icon: <Album />,
      title: 'Vinyl Quality',
      description: 'High bitrate audio that sounds like you\'re in the booth. Warm. Rich. Authentic.',
    },
    {
      icon: <Wifi />,
      title: 'Always Connected',
      description: 'Reliable infrastructure that keeps the music playing. 24/7. No dropouts.',
    },
  ];

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Scanlines />
      
      {/* Hero Section */}
      <Box 
        sx={{ 
          position: 'relative', 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          pt: { xs: 12, md: 0 },
        }}
      >
        <RetroGrid />
        
        {/* Floating Vinyl (Desktop Only) */}
        {!isMobile && (
          <Box
            sx={{
              position: 'absolute',
              right: '-2%',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.9,
              zIndex: 0,
            }}
          >
            <VinylRecord size={550} />
          </Box>
        )}
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              {/* Tagline */}
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Live DJ Streaming Platform
              </Typography>

              {/* Main Headline */}
              <Typography
                variant="h1"
                sx={{
                  color: 'text.primary',
                  mb: 3,
                }}
              >
                More Music.
                <br />
                <Box 
                  component="span" 
                  sx={{ 
                    color: 'primary.main',
                    display: 'inline-block',
                  }}
                >
                  <GlitchText>More Paid Sets.</GlitchText>
                </Box>
              </Typography>

              {/* Subheadline */}
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  mb: 5,
                  maxWidth: 500,
                  lineHeight: 1.8,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                SetSync helps venues add responsibly paid DJ programming when an
                in-room booking is impractical, while creating new paid
                opportunities for working DJs.
              </Typography>

              {/* CTA Buttons */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 6,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Start Streaming
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/login"
                  sx={{
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Login
                </Button>
              </Box>

              {/* Stats */}
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: 'primary.main' }} />
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    gap: { xs: 4, md: 6 },
                  }}
                >
                  <StatDisplay count={userCounts.venueCount} label="Venues" />
                  <StatDisplay count={userCounts.djCount} label="DJs" />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: { xs: 10, md: 16 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 8 }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              The Setup
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'text.primary',
              }}
            >
              How It Works
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <FeatureCard {...feature} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Capabilities Section */}
      <Box 
        sx={{ 
          py: { xs: 10, md: 16 }, 
          bgcolor: 'secondary.dark',
          borderTop: '2px solid',
          borderBottom: '2px solid',
          borderColor: 'secondary.main',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              The Tech
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'text.primary',
              }}
            >
              Built For DJs
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {capabilities.map((capability, index) => (
              <Grid item xs={12} md={4} key={index}>
                <FeatureCard {...capability} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 12, md: 20 }, position: 'relative' }}>
        {/* Background Vinyl */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: 0.1,
            zIndex: 0,
          }}
        >
          <VinylRecord size={600} spinning={false} />
        </Box>
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box 
            sx={{ 
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              border: '2px solid',
              borderColor: 'secondary.light',
              bgcolor: 'rgba(13, 13, 13, 0.9)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: '10%',
                right: '10%',
                height: 4,
                bgcolor: 'primary.main',
              },
            }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'text.primary',
                mb: 3,
              }}
            >
              Ready to Go Live?
            </Typography>
            <Typography 
              sx={{ 
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                mb: 5,
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Join the movement. Stream your sound. Build your legacy.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              endIcon={<ArrowForward />}
              sx={{
                px: 5,
                py: 1.5,
              }}
            >
              Get Started Free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          py: 6, 
          borderTop: '2px solid',
          borderColor: 'secondary.main',
          bgcolor: 'secondary.dark',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <img 
                  src={logoImage} 
                  alt="SetSync" 
                  style={{ height: '40px' }} 
                />
              </Box>
              <Typography 
                sx={{ 
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.8rem',
                  lineHeight: 1.8,
                }}
              >
                Local-first venue programming that creates new paid opportunities
                for working DJs.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography 
                sx={{ 
                  color: 'primary.main', 
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link 
                  to="/register" 
                  style={{ textDecoration: 'none' }}
                >
                  <Typography 
                    sx={{ 
                      color: 'text.secondary', 
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    Sign Up
                  </Typography>
                </Link>
                <Link 
                  to="/login" 
                  style={{ textDecoration: 'none' }}
                >
                  <Typography 
                    sx={{ 
                      color: 'text.secondary', 
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    Login
                  </Typography>
                </Link>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography 
                sx={{ 
                  color: 'primary.main', 
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Explore
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Link 
                  to="/venues" 
                  style={{ textDecoration: 'none' }}
                >
                  <Typography 
                    sx={{ 
                      color: 'text.secondary', 
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    For Venues
                  </Typography>
                </Link>
                <Link 
                  to="/djs" 
                  style={{ textDecoration: 'none' }}
                >
                  <Typography 
                    sx={{ 
                      color: 'text.secondary', 
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    For DJs
                  </Typography>
                </Link>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography 
                sx={{ 
                  color: 'primary.main', 
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography 
                  component="a" 
                  href="mailto:brian@setsync.io" 
                  sx={{ 
                    color: 'text.secondary', 
                    textDecoration: 'none',
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  brian@setsync.io
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box 
            sx={{ 
              mt: 8, 
              pt: 4, 
              borderTop: '1px solid', borderColor: 'secondary.main',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Typography 
              sx={{ 
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
              }}
            >
              © {new Date().getFullYear()} SetSync. All rights reserved.
            </Typography>
            <Typography 
              sx={{ 
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
              }}
            >
              MADE FOR THE UNDERGROUND
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
